// Usando fetch nativo en lugar de axios para mejor compatibilidad móvil
// Usar proxy PHP en producción para evitar problemas CORS en móvil
const USE_PROXY = window.location.hostname !== 'localhost';
const API_URL = USE_PROXY
  ? '/api-proxy.php?path='
  : 'https://misgolpes-backend-production.up.railway.app/api';

const buildUrl = (path) => {
  if (USE_PROXY) {
    // Remover el /api/ inicial del path para el proxy
    const cleanPath = path.replace('/api/', '').replace(/^\//, '');
    return `${API_URL}${cleanPath}`;
  }
  return `${API_URL}${path}`;
};

const almacen = (storage, clave) => ({
  get: () => {
    try {
      return window[storage].getItem(clave);
    } catch {
      return null;
    }
  },
  set: (valor) => {
    try {
      window[storage].setItem(clave, valor);
    } catch {
      // Navegación privada o almacenamiento bloqueado: se sigue sin recordar
    }
  },
  clear: () => {
    try {
      window[storage].removeItem(clave);
    } catch {
      // Nada que limpiar
    }
  }
});

// Testigo del panel de administración: en sessionStorage, se pierde al cerrar
// la pestaña. El del jugador va en localStorage, para no tener que entrar cada
// vez que se abre la app en el campo.
export const adminToken = almacen('sessionStorage', 'adminToken');
export const sesionToken = almacen('localStorage', 'sesionToken');

// El panel usa su propio testigo; el resto de la app, el del jugador
const esRutaDeAdmin = (path, metodo) =>
  path.startsWith('/admin') || (path.startsWith('/campos') && metodo !== 'GET');

const fetchWithTimeout = async (path, options = {}, timeout = 30000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  const url = buildUrl(path);
  const metodo = options.method || 'GET';
  const token = esRutaDeAdmin(path, metodo) ? adminToken.get() : sesionToken.get();

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers
      }
    });

    clearTimeout(id);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Error en la petición' }));
      const err = new Error(error.error || `HTTP Error ${response.status}`);
      err.response = { status: response.status, data: error };
      throw err;
    }

    const data = await response.json();
    // Retornar en formato compatible con axios (con .data)
    return { data };
  } catch (error) {
    clearTimeout(id);
    console.error('API Error:', {
      url,
      method: options.method || 'GET',
      message: error.message,
      name: error.name
    });
    throw error;
  }
};

// Campos
export const camposAPI = {
  getAll: () => fetchWithTimeout('/campos'),
  getActual: () => fetchWithTimeout('/campos/actual'),
  getCercanos: (lat, lng, limit = 20, maxDistance = 100) =>
    fetchWithTimeout(`/campos/cercanos/${lat}/${lng}?limit=${limit}&maxDistance=${maxDistance}`),
  getById: (id) => fetchWithTimeout(`/campos/${id}`),
  create: (data) => fetchWithTimeout('/campos', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  update: (id, data) => fetchWithTimeout(`/campos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (id) => fetchWithTimeout(`/campos/${id}`, {
    method: 'DELETE'
  }),
  getHoyos: (id) => fetchWithTimeout(`/campos/${id}/hoyos`),
  addHoyo: (id, data) => fetchWithTimeout(`/campos/${id}/hoyos`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  updateHoyo: (campoId, hoyoId, data) => fetchWithTimeout(`/campos/${campoId}/hoyos/${hoyoId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  deleteHoyo: (campoId, hoyoId) => fetchWithTimeout(`/campos/${campoId}/hoyos/${hoyoId}`, {
    method: 'DELETE'
  })
};

// Panel de administración
export const adminAPI = {
  login: (usuario, password) => fetchWithTimeout('/admin/login', {
    method: 'POST',
    body: JSON.stringify({ usuario, password })
  }),
  comprobarSesion: () => fetchWithTimeout('/admin/sesion'),
  resumen: () => fetchWithTimeout('/admin/resumen'),
  partidas: (limite = 50, desde = 0) => fetchWithTimeout(`/admin/partidas?limite=${limite}&desde=${desde}`),
  partida: (codigo) => fetchWithTimeout(`/admin/partidas/${codigo}`),
  borrarPartida: (codigo) => fetchWithTimeout(`/admin/partidas/${codigo}`, { method: 'DELETE' }),
  usuarios: () => fetchWithTimeout('/admin/usuarios'),
  restablecerPassword: (id, password) => fetchWithTimeout(`/admin/usuarios/${id}/password`, {
    method: 'PUT',
    body: JSON.stringify({ password })
  }),
  borrarUsuario: (id) => fetchWithTimeout(`/admin/usuarios/${id}`, { method: 'DELETE' })
};

// Cuenta del jugador (opcional: se puede jugar sin registrarse)
export const usuariosAPI = {
  registro: (datos) => fetchWithTimeout('/usuarios/registro', {
    method: 'POST',
    body: JSON.stringify(datos)
  }),
  login: (email, password) => fetchWithTimeout('/usuarios/login', {
    method: 'POST',
    body: JSON.stringify({ email, password })
  }),
  perfil: () => fetchWithTimeout('/usuarios/yo'),
  actualizarPerfil: (datos) => fetchWithTimeout('/usuarios/yo', {
    method: 'PUT',
    body: JSON.stringify(datos)
  }),
  cambiarPassword: (actual, nueva) => fetchWithTimeout('/usuarios/yo/password', {
    method: 'PUT',
    body: JSON.stringify({ actual, nueva })
  }),
  misPartidas: () => fetchWithTimeout('/usuarios/yo/partidas'),
  misEstadisticas: () => fetchWithTimeout('/usuarios/yo/estadisticas')
};

// Partidas
export const partidasAPI = {
  create: (data) => fetchWithTimeout('/partidas', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  getByCode: (codigo) => fetchWithTimeout(`/partidas/${codigo}`),
  join: (codigo, data) => fetchWithTimeout(`/partidas/${codigo}/join`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  start: (codigo) => fetchWithTimeout(`/partidas/${codigo}/start`, {
    method: 'POST'
  }),
  finish: (codigo) => fetchWithTimeout(`/partidas/${codigo}/finish`, {
    method: 'POST'
  }),
  getRanking: (codigo) => fetchWithTimeout(`/partidas/${codigo}/ranking`),
  getEstadisticas: (codigo) => fetchWithTimeout(`/partidas/${codigo}/estadisticas`)
};

// Golpes
export const golpesAPI = {
  incrementar: (data) => fetchWithTimeout('/golpes/incrementar', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  decrementar: (data) => fetchWithTimeout('/golpes/decrementar', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  registrarDrop: (data) => fetchWithTimeout('/golpes/drop', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  registrarMulligan: (data) => fetchWithTimeout('/golpes/mulligan', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  getByJugador: (jugadorId) => fetchWithTimeout(`/golpes/jugador/${jugadorId}`),
  finalizarHoyo: (data) => fetchWithTimeout('/golpes/finalizar', {
    method: 'POST',
    body: JSON.stringify(data)
  })
};

const api = { camposAPI, partidasAPI, golpesAPI, adminAPI, usuariosAPI };

export default api;
